/**
 * Rental Statuses Hook for ArendaPro+
 * 
 * Provides CRUD operations for rental statuses using the new domain-specific table.
 * Replaces the generic tags system for rental status management.
 * 
 * Following STYLEGUIDE.md patterns:
 * - Uses supabaseWorker for data operations
 * - Proper error handling and loading states
 * - React Query for caching and synchronization
 * - TypeScript strict typing
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { useAuth } from '@/context/AuthContext';
import { 
  RentalStatus, 
  RentalStatusDb, 
  RentalStatusUpdate,
  DomainStatusOption,
  DomainStatusFilters
} from '@/types/domain-statuses';
import {
  rentalStatusDbToClient,
  rentalStatusClientToDbInsert,
  rentalStatusUpdateToDb,
  statusesToOptions,
  validateStatusName,
  validateColorCode,
  sanitizeStatusInput
} from '@/lib/mappers/domainStatusMappers';
import { toast } from 'sonner';
import { useLogger } from '@/utils/logging/logger';
import { getNextStatusOrderValue } from './utils/orderHelpers';

// =============================================================================
// QUERY KEYS
// =============================================================================

const RENTAL_STATUSES_QUERY_KEY = 'rentalStatuses';

export const rentalStatusesKeys = {
  all: [RENTAL_STATUSES_QUERY_KEY] as const,
  lists: () => [...rentalStatusesKeys.all, 'list'] as const,
  list: (filters: DomainStatusFilters) => [...rentalStatusesKeys.lists(), filters] as const,
  details: () => [...rentalStatusesKeys.all, 'detail'] as const,
  detail: (id: string) => [...rentalStatusesKeys.details(), id] as const,
};

// =============================================================================
// MAIN HOOK
// =============================================================================

export type UseRentalStatusesOptions = {
  filters?: DomainStatusFilters;
  enabled?: boolean;
};

type UseRentalStatusesResult = {
  rentalStatuses: RentalStatus[];
  options: DomainStatusOption[];
  defaultStatus: RentalStatus | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => unknown;
  create: (newStatus: Omit<RentalStatus, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => void;
  update: (args: { id: string; updates: RentalStatusUpdate }) => void;
  delete: (id: string) => void;
  reorder: (items: Array<{ id: string; sortOrder: number }>) => void;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isReordering: boolean;
  createError: unknown;
  updateError: unknown;
  deleteError: unknown;
  reorderError: unknown;
};

export function useRentalStatuses(options: UseRentalStatusesOptions = {}): UseRentalStatusesResult {
  const logger = useLogger('useRentalStatuses');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { filters = {}, enabled = true } = options;

  // =============================================================================
  // QUERIES
  // =============================================================================

  const {
    data: rentalStatuses = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: rentalStatusesKeys.list(filters),
    queryFn: async (): Promise<RentalStatus[]> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      let query = supabaseWorker
        .from('rental_statuses')
        .select('*')
        .eq('user_id', user.id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (filters.search) {
        query = query.ilike('name', `%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) {
        logger.error('Error fetching rental statuses', error);
        const msg = (error as { message?: string } | null)?.message || 'unknown';
        throw new Error(`Failed to fetch rental statuses: ${msg}`);
      }

      return (data as RentalStatusDb[]).map(rentalStatusDbToClient);
    },
    enabled: enabled && !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // =============================================================================
  // MUTATIONS
  // =============================================================================

  const createMutation = useMutation({
    mutationFn: async (newStatus: Omit<RentalStatus, 'id' | 'createdAt' | 'updatedAt' | 'userId'>): Promise<RentalStatus> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Validate input
      const sanitized = sanitizeStatusInput(newStatus);
      
      if (!validateStatusName(sanitized.name || '')) {
        throw new Error('Название статуса должно содержать от 1 до 100 символов');
      }

      if (sanitized.color && !validateColorCode(sanitized.color)) {
        throw new Error('Некорректный формат цвета. Используйте HEX формат (например, #10b981)');
      }

      // Check for duplicates
      const existingStatus = rentalStatuses.find(
        status => status.name.toLowerCase() === sanitized.name?.toLowerCase()
      );
      
      if (existingStatus) {
        throw new Error('Статус с таким названием уже существует');
      }

      const name = sanitized.name ?? newStatus.name?.trim() ?? '';
      if (!name) {
        throw new Error('Название статуса должно содержать от 1 до 100 символов');
      }

      const color = sanitized.color ?? newStatus.color ?? '#3b82f6';
      const description = sanitized.description ?? newStatus.description?.trim();
      const nextOrder = await getNextStatusOrderValue('rental_statuses', user.id);

      const insertPayload: Omit<RentalStatus, 'id' | 'createdAt' | 'updatedAt'> & { userId: string } = {
        name,
        color,
        sortOrder: nextOrder,
        userId: user.id,
        description: description ?? undefined,
      };

      const insertData = rentalStatusClientToDbInsert(insertPayload);

      const { data, error } = await supabaseWorker
        .from('rental_statuses')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        logger.error('Error creating rental status', error);
        const msg = (error as { message?: string } | null)?.message || 'unknown';
        throw new Error(`Failed to create rental status: ${msg}`);
      }

      return rentalStatusDbToClient(data as RentalStatusDb);
    },
    onSuccess: (newStatus) => {
      // Invalidate and refetch rental statuses
      queryClient.invalidateQueries({ queryKey: rentalStatusesKeys.all });
      toast.success(`Статус аренды "${newStatus.name}" создан`);
    },
    onError: (error: Error) => {
      logger.error('Error creating rental status', error);
      toast.error(error.message || 'Ошибка при создании статуса аренды');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: RentalStatusUpdate }): Promise<RentalStatus> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Validate input
      const sanitized = sanitizeStatusInput(updates);

      if (sanitized.name && !validateStatusName(sanitized.name)) {
        throw new Error('Название статуса должно содержать от 1 до 100 символов');
      }

      if (sanitized.color && !validateColorCode(sanitized.color)) {
        throw new Error('Некорректный формат цвета. Используйте HEX формат (например, #10b981)');
      }

      // Check for duplicates (excluding current status)
      if (sanitized.name) {
        const existingStatus = rentalStatuses.find(
          status => status.id !== id && status.name.toLowerCase() === sanitized.name?.toLowerCase()
        );
        
        if (existingStatus) {
          throw new Error('Статус с таким названием уже существует');
        }
      }

      const updateData = rentalStatusUpdateToDb(sanitized);

      const { data, error } = await supabaseWorker
        .from('rental_statuses')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        logger.error('Error updating rental status', error);
        const msg = (error as { message?: string } | null)?.message || 'unknown';
        throw new Error(`Failed to update rental status: ${msg}`);
      }

      if (!data) {
        throw new Error('Rental status not found or access denied');
      }

      return rentalStatusDbToClient(data as RentalStatusDb);
    },
    onSuccess: (updatedStatus) => {
      // Invalidate and refetch rental statuses
      queryClient.invalidateQueries({ queryKey: rentalStatusesKeys.all });
      toast.success(`Статус аренды "${updatedStatus.name}" обновлен`);
    },
    onError: (error: Error) => {
      logger.error('Error updating rental status', error);
      toast.error(error.message || 'Ошибка при обновлении статуса аренды');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabaseWorker
        .from('rental_statuses')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        logger.error('Error deleting rental status', error);
        const msg = (error as { message?: string } | null)?.message || 'unknown';
        throw new Error(`Failed to delete rental status: ${msg}`);
      }
    },
    onSuccess: (_, deletedId) => {
      // Find the deleted status name for toast
      const deletedStatus = rentalStatuses.find(status => status.id === deletedId);
      
      // Invalidate and refetch rental statuses
      queryClient.invalidateQueries({ queryKey: rentalStatusesKeys.all });
      toast.success(`Статус аренды "${deletedStatus?.name || ''}" удален`);
    },
    onError: (error: Error) => {
      logger.error('Error deleting rental status', error);
      toast.error(error.message || 'Ошибка при удалении статуса аренды');
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (items: Array<{ id: string; sortOrder: number }>): Promise<void> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Update each item's sort_order directly via Supabase
      const updates = items.map(item =>
        supabaseWorker
          .from('rental_statuses')
          .update({ sort_order: item.sortOrder })
          .eq('id', item.id)
          .eq('user_id', user.id)
      );

      const results = await Promise.all(updates);
      
      // Check for errors
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        console.error('Error reordering rental statuses:', errors);
        throw new Error('Failed to reorder some items');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rentalStatusesKeys.all });
      toast.success('Порядок статусов обновлен');
    },
    onError: (error: Error) => {
      console.error('Error reordering rental statuses:', error);
      toast.error(error.message || 'Ошибка при изменении порядка статусов');
    },
  });

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  const statusOptions: DomainStatusOption[] = statusesToOptions(rentalStatuses);

  const defaultStatus = rentalStatuses.find(status => 
    status.name.toLowerCase() === 'активно' || 
    status.name.toLowerCase() === 'active'
  );

  // =============================================================================
  // RETURN INTERFACE
  // =============================================================================

  return {
    // Data
    rentalStatuses,
    options: statusOptions,
    defaultStatus,
    
    // State
    isLoading,
    error,
    
    // Actions
    refetch,
    create: createMutation.mutate,
    update: updateMutation.mutate,
    delete: deleteMutation.mutate,
    reorder: reorderMutation.mutate,
    
    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isReordering: reorderMutation.isPending,
    
    // Mutation errors
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
    reorderError: reorderMutation.error,
  };
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook for getting rental status options for selectors
 */
export function useRentalStatusOptions(filters?: DomainStatusFilters): {
  options: DomainStatusOption[];
  isLoading: boolean;
  error: unknown;
} {
  const { options, isLoading, error } = useRentalStatuses({ filters });
  
  return {
    options,
    isLoading,
    error,
  };
}

/**
 * Hook for getting a single rental status by ID
 */
export function useRentalStatus(id: string | undefined): ReturnType<typeof useQuery<RentalStatus | null, Error>> {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: rentalStatusesKeys.detail(id || ''),
    queryFn: async (): Promise<RentalStatus | null> => {
      if (!user?.id || !id) {
        return null;
      }

      const { data, error } = await supabaseWorker
        .from('rental_statuses')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();

      if (error) {
        const code = (error as { code?: string } | null)?.code;
        if (code === 'PGRST116') {
          return null; // Not found
        }
        const msg = (error as { message?: string } | null)?.message || 'unknown';
        throw new Error(`Failed to fetch rental status: ${msg}`);
      }

      return rentalStatusDbToClient(data as RentalStatusDb);
    },
    enabled: !!user?.id && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
